import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'react-hot-toast';
import { AnimatePresence } from 'framer-motion';
import { useAuthListener } from './hooks/useAuthListener';
import { useAuthStore } from './store';
import { PageLoader } from './components/common/Loader';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import CartDrawer from './components/layout/CartDrawer';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import AuthPage from './pages/AuthPage';
import WishlistPage from './pages/WishlistPage';
import AccountLayout from './pages/AccountPage';
import FAQsPage from './pages/FAQsPage';
import { CollectionsPage, CollectionDetailPage } from './pages/CollectionsPage';
import { AboutPage, ContactPage, ShippingPolicyPage, PrivacyPolicyPage, TermsPage } from './pages/StaticPages';
import AdminLayout from './pages/admin/AdminLayout';
import ScrollTop from './components/common/ScrollTop';
import PixelManager from './components/common/PixelManager';
import NumberInputWheelGuard from './components/common/NumberInputWheelGuard';

const AppContent = () => {
  useAuthListener();
  const { loading } = useAuthStore();
  if (loading) return <PageLoader />;
  return (
    <BrowserRouter>
      <PixelManager />
      <NumberInputWheelGuard />
      <Toaster position="top-right" toastOptions={{
        duration: 3000,
        style: { fontFamily: 'DM Sans, sans-serif', fontSize: '14px', borderRadius: '0', boxShadow: '0 8px 32px rgba(0,0,0,0.12)' },
        success: { iconTheme: { primary: '#1a3a6e', secondary: '#fff' } },
        error: { iconTheme: { primary: '#e63946', secondary: '#fff' } },
      }} />
      <Routes>
        <Route path="/admin/*" element={<AdminLayout />} />
        <Route path="/*" element={
          <div className="flex flex-col min-h-screen">
            <ScrollTop />
            <Navbar />
            <CartDrawer />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/shop" element={<ShopPage />} />
                <Route path="/product/:id" element={<ProductDetailPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/login" element={<AuthPage />} />
                <Route path="/register" element={<AuthPage />} />
                <Route path="/wishlist" element={<WishlistPage />} />
                <Route path="/account/*" element={<AccountLayout />} />
                <Route path="/collections" element={<CollectionsPage />} />
                <Route path="/collections/:slug" element={<CollectionDetailPage />} />
                <Route path="/faqs" element={<FAQsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="*" element={
                  <div className="min-h-[70vh] flex items-center justify-center text-center px-4">
                    <div>
                      <div className="font-display text-[140px] font-black leading-none" style={{ color: '#1a3a6e', opacity: 0.07 }}>404</div>
                      <h1 className="font-display text-3xl font-black text-gray-900 mb-2 -mt-8">Page Not Found</h1>
                      <p className="text-gray-500 mb-6">This page doesn't exist.</p>
                      <a href="/" className="btn-primary">Go Home</a>
                    </div>
                  </div>
                } />
              </Routes>
            </main>
            <Footer />
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
};

const App = () => (
  <HelmetProvider><AppContent /></HelmetProvider>
);
export default App;
